/**
 * IAN Assist — Entry Point
 *
 * Boots the React app inside Office.onReady.
 * Detects Outlook theme (light/dark) and wraps in FluentProvider
 * with the correct IAN branded theme.
 */
import * as React from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider } from "@fluentui/react-components";
import App from "./components/App";
import { ianLightTheme } from "./theme/ianLightTheme";
import { ianDarkTheme } from "./theme/ianDarkTheme";
import { injectIanTokens } from "./theme/ianTokens";

/* global document, Office, module, require */

/**
 * Detect Outlook theme from officeTheme API.
 */
function detectTheme() {
  try {
    const officeTheme = Office.context.officeTheme;
    if (officeTheme && officeTheme.bodyBackgroundColor) {
      const hex = officeTheme.bodyBackgroundColor.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luminance < 0.5 ? "dark" : "light";
    }
  } catch {
    // officeTheme not available
  }
  return "light";
}

/**
 * Root wrapper that manages theme state.
 */
function Root() {
  const [themeMode, setThemeMode] = React.useState("light");

  React.useEffect(() => {
    // Initial detection
    const detected = detectTheme();
    setThemeMode(detected);
    injectIanTokens(detected);

    // Subscribe to theme changes
    try {
      Office.context.document?.addHandlerAsync(
        Office.EventType.OfficeThemeChanged,
        (event) => {
          try {
            const hex = event.officeTheme?.bodyBackgroundColor?.replace("#", "") || "";
            if (hex.length === 6) {
              const r = parseInt(hex.substring(0, 2), 16) / 255;
              const g = parseInt(hex.substring(2, 4), 16) / 255;
              const b = parseInt(hex.substring(4, 6), 16) / 255;
              const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
              const newMode = lum < 0.5 ? "dark" : "light";
              setThemeMode(newMode);
              injectIanTokens(newMode);
            }
          } catch {
            // ignore
          }
        }
      );
    } catch {
      // OfficeThemeChanged not supported
    }
  }, []);

  const fluentTheme = themeMode === "dark" ? ianDarkTheme : ianLightTheme;

  return (
    <FluentProvider theme={fluentTheme}>
      <App />
    </FluentProvider>
  );
}

// ── Boot ──
const rootElement = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

Office.onReady(() => {
  root?.render(<Root />);
});

if (module.hot) {
  module.hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(
      <FluentProvider theme={ianLightTheme}>
        <NextApp />
      </FluentProvider>
    );
  });
}
